import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, MessageCircle, Quote, Sparkles, TrendingUp } from "lucide-react";
import { getSegmentBySlug, SEGMENT_CASES, type SegmentCase } from "@/lib/segments-data";
import { getIAForSegment } from "@/lib/segment-ia-map";
import { KENTRO } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { trackIaNav, trackEvent, withUtm } from "@/lib/analytics";

export const Route = createFileRoute("/segmentos/$slug")({
  loader: ({ params }) => {
    const segment = getSegmentBySlug(params.slug);
    if (!segment) throw notFound();
    return { slug: segment.slug };
  },
  head: ({ params }) => {
    const s = getSegmentBySlug(params.slug);
    const baseUrl = "https://kentro.com.br";
    const url = `${baseUrl}/segmentos/${params.slug}`;
    const title = s
      ? `Kentro para ${s.name} — Atendimento omnichannel com IA e WhatsApp Oficial`
      : "Segmento — Kentro";
    const description = s
      ? `${s.tagline} Resultados reais em ${s.name.toLowerCase()}: ${s.metrics
          .slice(0, 3)
          .map((m) => `${m.value} ${m.label}`)
          .join("; ")}.`.slice(0, 300)
      : "Cases de sucesso da Kentro Sistemas.";

    const jsonLd = s
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          about: s.name,
          articleSection: s.category,
          inLanguage: "pt-BR",
          author: { "@type": "Organization", name: "Kentro Sistemas" },
          publisher: {
            "@type": "Organization",
            name: "Kentro Sistemas",
            logo: { "@type": "ImageObject", url: `${baseUrl}/apple-touch-icon.png` },
          },
        }
      : null;

    const breadcrumb = s
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
            { "@type": "ListItem", position: 2, name: "Segmentos", item: `${baseUrl}/#segmentos` },
            { "@type": "ListItem", position: 3, name: s.name, item: url },
          ],
        }
      : null;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: s ? [{ rel: "canonical", href: url }] : [],
      scripts: jsonLd
        ? [
            { type: "application/ld+json", children: JSON.stringify(jsonLd) },
            { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
          ]
        : [],
    };
  },
  component: SegmentPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-2xl font-bold">Segmento não encontrado</h1>
      <Link to="/" className="mt-4 inline-block text-primary-glow">Voltar para a home</Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-2xl font-bold">Erro ao carregar o segmento</h1>
    </div>
  ),
});

function SegmentPage() {
  const { slug } = Route.useLoaderData();
  const segment = getSegmentBySlug(slug) as SegmentCase;
  const Icon = segment.icon;
  const related = SEGMENT_CASES.filter((s) => s.slug !== segment.slug).slice(0, 3);
  const ia = getIAForSegment(segment.slug);

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5 py-20 md:py-28">
        <div className="absolute inset-0 -z-10 opacity-40" style={{ background: "var(--gradient-hero)" }} />
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/" hash="segmentos" className="hover:text-foreground">Segmentos</Link>
            <span>/</span>
            <span className="text-foreground">{segment.shortName}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span className="gradient-primary inline-flex size-14 items-center justify-center rounded-2xl text-white shadow-glow">
              <Icon className="size-7" />
            </span>
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Case · {segment.category}
            </span>
          </div>

          <h1 className="mt-6 text-4xl font-bold leading-tight md:text-5xl" style={{ fontFamily: "var(--font-display)" }}>
            Kentro para <span className="gradient-text">{segment.name}</span>
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{segment.tagline}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="gradient-primary text-white shadow-glow">
              <Link to="/" hash="contato">
                Agendar demonstração <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href={KENTRO.whatsappComercialUrl} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-1 size-4" /> Falar no WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* IA do segmento */}
      {ia && (
        <section className="border-b border-white/5 py-12 md:py-14">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="relative overflow-hidden rounded-3xl border border-primary-glow/20 bg-gradient-to-br from-primary/15 via-background to-primary-glow/10 p-6 md:p-8"
            >
              <div className="absolute -right-16 -top-16 size-56 rounded-full bg-primary/30 blur-3xl" aria-hidden />
              <div className="relative grid items-center gap-5 md:grid-cols-[1fr_auto]">
                <div>
                  <span className="inline-flex items-center gap-2 rounded-full border border-primary-glow/30 bg-background/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-glow">
                    <Sparkles className="size-3.5" /> Teste agora · {ia.name}
                  </span>
                  <h2 className="mt-3 text-xl font-bold md:text-2xl" style={{ fontFamily: "var(--font-display)" }}>
                    Converse com a {ia.name} no WhatsApp
                  </h2>
                  <p className="mt-2 max-w-xl text-sm text-muted-foreground md:text-base">
                    IA treinada para {segment.shortName.toLowerCase()}. Faça perguntas reais, teste objeções
                    e veja como ela atende o seu cliente — em menos de 30 segundos.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 md:self-center">
                  <Button asChild className="gradient-primary text-white shadow-glow">
                    <a
                      href={withUtm(ia.url, {
                        utm_source: "site_kentro",
                        utm_medium: "segment_page",
                        utm_campaign: "experimente_ia",
                        utm_content: ia.slug,
                      })}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        trackIaNav("segment_page_cta", segment.slug, { ia_name: ia.name, ia_slug: ia.slug });
                        trackEvent("ia_try_click", {
                          ia_name: ia.name,
                          ia_slug: ia.slug,
                          ia_segment: segment.name,
                          location: "segment_page",
                          segment: segment.slug,
                        });
                      }}
                    >
                      Experimente agora <ArrowRight className="ml-1 size-4" />
                    </a>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/ia" onClick={() => trackIaNav("segment_page_all_ias", segment.slug)}>
                      Ver todas as IAs
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}


      {/* Metrics */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary-glow">
            <TrendingUp className="size-4" /> Resultados reais
          </div>
          <h2 className="mt-2 text-2xl font-bold md:text-3xl" style={{ fontFamily: "var(--font-display)" }}>
            O que muda na operação
          </h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {segment.metrics.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="glass-card rounded-2xl p-5"
              >
                <div className="gradient-text text-3xl font-bold leading-none break-words" style={{ fontFamily: "var(--font-display)" }}>
                  {m.value}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{m.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="border-y border-white/5 bg-white/[0.02] py-16 md:py-20">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
          <div className="glass-card rounded-2xl p-6 md:p-8">
            <div className="text-xs font-semibold uppercase tracking-wide text-red-400/90">O problema</div>
            <h3 className="mt-2 text-xl font-bold md:text-2xl" style={{ fontFamily: "var(--font-display)" }}>
              A dor de quem atende {segment.shortName.toLowerCase()}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
              {segment.challenges}
            </p>
          </div>
          <div className="glass-card rounded-2xl p-6 md:p-8">
            <div className="text-xs font-semibold uppercase tracking-wide text-primary-glow">A solução Kentro</div>
            <h3 className="mt-2 text-xl font-bold md:text-2xl" style={{ fontFamily: "var(--font-display)" }}>
              Como resolvemos
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
              {segment.solutions}
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" />Painel omnichannel com WhatsApp, telefone, Instagram e e-mail.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" />Assistente de IA 24h treinado para o seu segmento.</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" />CRM com classificação de leads e histórico completo.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold md:text-3xl" style={{ fontFamily: "var(--font-display)" }}>
            Quem usa, <span className="gradient-text">conta.</span>
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {segment.testimonials.map((t, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="glass-card flex h-full flex-col rounded-2xl p-6"
              >
                <Quote className="size-6 text-primary-glow/80" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground/90">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-4 border-t border-white/5 pt-3">
                  <div className="text-sm font-semibold">{t.author}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <div className="glass-card rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl font-bold md:text-3xl" style={{ fontFamily: "var(--font-display)" }}>
              Quer esses resultados na sua operação?
            </h2>
            <p className="mt-3 text-muted-foreground">
              Em 20 minutos mostramos como a Kentro se encaixa no dia a dia de {segment.shortName.toLowerCase()}.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button asChild size="lg" className="gradient-primary text-white shadow-glow">
                <Link to="/" hash="contato">Agendar demonstração</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href={KENTRO.whatsappComercialUrl} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-1 size-4" /> Falar no WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="border-t border-white/5 py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-semibold">Outros segmentos</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {related.map((r) => {
              const RIcon = r.icon;
              return (
                <Link
                  key={r.slug}
                  to="/segmentos/$slug"
                  params={{ slug: r.slug }}
                  className="glass-card group flex items-center gap-3 rounded-xl p-4 transition hover:-translate-y-0.5 hover:shadow-glow"
                >
                  <span className="gradient-primary inline-flex size-10 items-center justify-center rounded-lg text-white">
                    <RIcon className="size-4" />
                  </span>
                  <span className="flex-1 text-sm font-semibold">{r.name}</span>
                  <ArrowRight className="size-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary-glow" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
